import React from "react";

export type BaseStatus = "Available" | "Away" | "Offline";

/**
 * badge  — 17×17 px, 1.5 px white border, icon inside.
 *          Use as an avatar corner overlay.
 *          Caller should wrap with `relative` and position with `absolute -bottom-1 -right-1`.
 *
 * dot    — 10×10 px plain square, no icon.
 *          Use inline in lists, dropdowns, and row indicators.
 */
export interface StatusBadgeProps {
  /** Resolved base status */
  baseStatus: BaseStatus;
  /** Human-readable label — announced to screen readers and shown as tooltip */
  label: string;
  /** Hex color for custom statuses (overrides the base-status color) */
  customColor?: string;
  variant?: "badge" | "dot";
  className?: string;
}

// SVG icons — aria-hidden because the parent carries role="img" + aria-label
const CheckIcon = (
  <svg
    width="10"
    height="10"
    viewBox="0 0 10 10"
    fill="none"
    aria-hidden="true"
  >
    <polyline
      points="1.5,5 4,7.5 8.5,2"
      stroke="white"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ClockIcon = (
  <svg
    width="11"
    height="11"
    viewBox="0 0 11 11"
    fill="none"
    aria-hidden="true"
  >
    <line
      x1="5.5"
      y1="2.5"
      x2="5.5"
      y2="5.5"
      stroke="white"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <line
      x1="5.5"
      y1="5.5"
      x2="7.5"
      y2="7"
      stroke="white"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const DashIcon = (
  <svg width="10" height="2" viewBox="0 0 10 2" fill="none" aria-hidden="true">
    <line
      x1="1"
      y1="1"
      x2="9"
      y2="1"
      stroke="white"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const STATUS_CONFIG: Record<BaseStatus, { bg: string; icon: React.ReactNode }> =
  {
    Available: { bg: "bg-success-green-500", icon: CheckIcon },
    Away: { bg: "bg-sweet-orange-500", icon: ClockIcon },
    Offline: { bg: "bg-zinc-400", icon: DashIcon }
  };

export default function StatusBadge({
  baseStatus,
  label,
  customColor,
  variant = "badge",
  className = ""
}: StatusBadgeProps) {
  const config = STATUS_CONFIG[baseStatus] ?? STATUS_CONFIG.Offline;
  const isCustom = !!customColor;

  const bgClass = isCustom ? "" : config.bg;
  const bgStyle = isCustom ? { backgroundColor: customColor } : undefined;
  const icon = isCustom ? null : config.icon;

  if (variant === "dot") {
    return (
      <span
        className={`block rounded-badge flex-none ${bgClass} ${className}`}
        style={{ width: 10, height: 10, ...bgStyle }}
        role="img"
        aria-label={label}
        title={label}
      />
    );
  }

  // badge — 17 px with icon
  return (
    <div
      className={`w-[17px] h-[17px] rounded-badge border-badge border-white flex items-center justify-center ${bgClass} ${className}`}
      style={bgStyle}
      role="img"
      aria-label={label}
      title={label}
    >
      {icon}
    </div>
  );
}
